import { now } from '../Clock.ts';
import type { GameState } from '../game/GameState.ts';

/**
 * A controller turns game state into an EXECUTED flap.
 *
 * The single most important rule in this codebase:
 *
 *   `ControllerOutput.flap === true` means "a flap has actually happened" --
 *   a key went down, a switch closed, an EMG burst crossed threshold, a
 *   stimulated wrist moved and tripped a sensor.
 *
 *   It does NOT mean "something decided a flap would be a good idea".
 *
 * Intent lives in `BotController.decide()` and in the STIM_* event chain.
 * Execution lives here. Controllers that mediate intent through an actuator
 * (see `StimulationController`) return flap=false on the tick the bot decided,
 * and flap=true only later, when the physical response is detected.
 */
export interface ControllerOutput {
  flap: boolean;
  /** now() when the output was produced. */
  timestamp: number;
  metadata?: Record<string, unknown>;
}

export interface Controller {
  /** Stable machine id, used in logs and CSV exports. */
  readonly id: string;
  /** Human-readable name for the dashboard. */
  readonly label: string;

  update(state: GameState): ControllerOutput;

  /** Subscribe to DOM/hardware events. Called when the controller goes live. */
  attach?(): void | Promise<void>;
  /** Tear down every listener. Called when switching away. Must be idempotent. */
  detach?(): void | Promise<void>;
  /** Clear per-run state (pending actuations, edge latches) on game restart. */
  reset?(): void;
  /** Optional free-form status for the dashboard. */
  status?(): Record<string, unknown>;
}

export const NO_FLAP: ControllerOutput = { flap: false, timestamp: 0 };

export function noFlap(metadata?: Record<string, unknown>): ControllerOutput {
  return { flap: false, timestamp: now(), metadata };
}

export function doFlap(metadata?: Record<string, unknown>): ControllerOutput {
  return { flap: true, timestamp: now(), metadata };
}

/**
 * Latch that converts asynchronous edge events (keydown, switch closure,
 * EMG threshold crossing) into exactly one flap on the next simulation tick.
 *
 * Without this, a 120 Hz simulation would miss or double-count a 5 ms button
 * bounce. `consume()` also reports the wall-clock time of the edge so the
 * latency tracker can measure input -> flap separately from everything else.
 */
export class EdgeLatch {
  private pending = false;
  private edgeTimestamp = 0;
  private meta: Record<string, unknown> | undefined;

  set(timestamp = now(), meta?: Record<string, unknown>): void {
    if (!this.pending) {
      this.pending = true;
      this.edgeTimestamp = timestamp;
      this.meta = meta;
    }
  }

  /** Returns the edge timestamp and clears the latch, or null if no edge. */
  consume(): { timestamp: number; meta?: Record<string, unknown> } | null {
    if (!this.pending) return null;
    this.pending = false;
    const out = { timestamp: this.edgeTimestamp, meta: this.meta };
    this.meta = undefined;
    return out;
  }

  clear(): void {
    this.pending = false;
    this.meta = undefined;
  }

  get isPending(): boolean {
    return this.pending;
  }
}
